import { t } from '@avalara/bat-apps-shared/i18n';
import { SCardAction, SCol, SIcon, SRow } from '@avalara/skylab-sdk/react';

import '../product-catalog.css';

import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { ProductDetailSection, ProductFeatureCard } from '../../../../../types/product-detail';

const MEDIUM_SIZED_ICONS = new Set(['globe', 'lightbulb-lit', 'math', 'wrench']);

// Single source of truth for whether a given featureSections entry actually renders anything —
// mirrors this component's own `if (!items.length) return null` below. ProductDetailPage uses this
// same check to find which section is the real first one to render, so askAviPrompts (only ever
// meant for that one section, see below) can't be silently dropped on an empty leading section.
export const hasFeatureCards = (features: ProductDetailSection<ProductFeatureCard>): boolean =>
  features.items.length > 0;

type Props = {
  features: ProductDetailSection<ProductFeatureCard>;
  sectionIndex: number;
  // Trims this section's usual top margin/padding — for a hero-pricing product's first feature
  // section, which Figma places flush against the hero with no gap (see ProductDetailPage). Left
  // with a small pad-top-lg rather than none at all: a literal 0px reads as the heading touching
  // the hero's colored band once actually rendered, even though Figma's own spec is exactly 0.
  tightTopSpacing?: boolean;
  // Rendered inside this same section (not as its own top-level page section) so it shares this
  // section's internal spacing rather than the larger inter-section gap every other top-level
  // section gets from `[data-ref="buy.product-detail.sections"] > :not(:first-child)` — Figma
  // groups it directly under this section's own card row. ProductDetailPage only ever passes this
  // to the first featureSections entry that actually renders (per hasFeatureCards above), not
  // necessarily array index 0 — see ProductClientDetails.askAviPrompts.
  askAviPrompts?: Array<string>;
};

const ProductDetailFeatureCards = ({
  features: { heading, description, items },
  sectionIndex,
  tightTopSpacing,
  askAviPrompts,
}: Props) => {
  if (!hasFeatureCards({ heading, description, items })) return null;

  return (
    <div
      className={tightTopSpacing ? 'pad-top-lg' : 'margin-top-lg pad-top-xl'}
      data-ref={`buy.product-detail.feature-cards-section.${sectionIndex}`}
    >
      {heading && (
        <h2 className='product-detail-section-heading' data-ref={`buy.product-detail.features.${sectionIndex}.heading`}>
          {t(heading as TranslationKey)}
        </h2>
      )}
      {description && (
        <p
          className='margin-top-md margin-bottom-lg'
          data-ref={`buy.product-detail.features.${sectionIndex}.subheading`}
        >
          {t(description as TranslationKey)}
        </p>
      )}
      <SRow data-ref={`buy.product-detail.feature-cards.${sectionIndex}`}>
        {items.map((feature, index) => (
          <SCol
            className={index === 0 ? 'pad-left-none' : ''}
            key={feature.title}
            span='auto'
            data-ref={`buy.product-detail.feature-card.${sectionIndex}.${index}`}
          >
            <SCardAction className='product-detail-feature-card'>
              <div slot='title' className='flex flex-dir-col'>
                <span>
                  <SIcon
                    className={['product-detail-feature-card-icon', MEDIUM_SIZED_ICONS.has(feature.icon) && 'medium']
                      .filter(Boolean)
                      .join(' ')}
                    name={feature.icon}
                    aria-hidden='true'
                  />
                </span>

                <h3>{t(feature.title as TranslationKey)}</h3>
              </div>
              <div slot='description'>
                <p>{t(feature.description as TranslationKey)}</p>
              </div>
            </SCardAction>
          </SCol>
        ))}
      </SRow>
      {askAviPrompts && askAviPrompts.length > 0 && (
        <div className='margin-top-lg' data-ref={`buy.product-detail.ask-avi.${sectionIndex}`}>
          <p className='text-strong' data-ref={`buy.product-detail.ask-avi.${sectionIndex}.heading`}>
            {t('client.product-detail.ask-avi-heading')}
          </p>
          <div
            className='product-detail-ask-avi-chips margin-top-md'
            data-ref={`buy.product-detail.ask-avi-chips.${sectionIndex}`}
          >
            {askAviPrompts.map((prompt, index) => (
              <div
                className='product-detail-ask-avi-chip'
                key={prompt}
                data-ref={`buy.product-detail.ask-avi-chip.${sectionIndex}.${index}`}
              >
                <SIcon name='ai-assisted' aria-hidden='true' className='product-detail-ask-avi-chip-icon' />
                <span>{t(prompt as TranslationKey)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailFeatureCards;
